import type { BookingQuestion, BookingQuestionType } from '@shared/core/appointment-types'
import { countCodePoints } from '@shared/core/bookings'
import classNames from 'classnames'
import { SelectField, TextField, Typography } from 'src/modules/core/components'
import { CheckboxGroup } from 'src/modules/core/components/checkbox-group'
import { RadioGroup } from 'src/modules/core/components/radio-group'
import { SELECT_ONE_LABEL } from '../booking-details-step.constants'
import { getAnswerCharLimit, limitToCodePoints } from '../booking-details-step.domain'
import type { BookingQuestionFieldProps, BookingQuestionsFieldsProps } from './booking-questions-fields.interface'
import classes from './booking-questions-fields.module.scss'

/** A short answer shows its counter only from this share of the cap; a long answer always does. */
const SHORT_TEXT_COUNTER_FROM = 0.8

type SelectOption = { value: string; label: string }

const QUESTION_FIELDS: Record<BookingQuestionType, (props: BookingQuestionFieldProps) => JSX.Element> = {
  short_text: TextQuestionField,
  long_text: TextQuestionField,
  dropdown: DropdownQuestionField,
  multiple_choice: MultipleChoiceQuestionField,
  checkboxes: CheckboxesQuestionField,
}

export function BookingQuestionsFields({ questions, answers, errors, onChange, onBlur }: BookingQuestionsFieldsProps) {
  return (
    <>
      {questions.map((question) => {
        const QuestionField = QUESTION_FIELDS[question.type]
        return (
          <QuestionField
            key={question.id}
            question={question}
            value={answers[question.id]}
            error={errors[question.id] ?? null}
            onChange={(value) => onChange(question, value)}
            onBlur={() => onBlur(question)}
          />
        )
      })}
    </>
  )
}

function TextQuestionField({ question, value, error, onChange, onBlur }: BookingQuestionFieldProps) {
  const fieldId = questionFieldId(question)
  const limit = getAnswerCharLimit(question.type) ?? 0
  const text = typeof value === 'string' ? value : ''
  const count = countCodePoints(text)
  const isLongText = question.type === 'long_text'
  const showCounter = isLongText || count >= limit * SHORT_TEXT_COUNTER_FROM
  const counterId = `${fieldId}-counter`
  const errorId = `${fieldId}-error`
  return (
    <TextField
      id={fieldId}
      Element={isLongText ? 'textarea' : 'input'}
      rows={isLongText ? 4 : undefined}
      label={question.label}
      labelAddOn={question.required ? <RequiredMark /> : undefined}
      value={text}
      onChange={(event) => onChange(limitToCodePoints(event.target.value, limit))}
      onBlur={onBlur}
      error={error ?? undefined}
      errorId={errorId}
      hint={showCounter ? <CharacterCounter count={count} limit={limit} /> : undefined}
      hintId={counterId}
      aria-required={question.required || undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(showCounter ? counterId : null, error ? errorId : null)}
    />
  )
}

function DropdownQuestionField({ question, value, error, onChange, onBlur }: BookingQuestionFieldProps) {
  const fieldId = questionFieldId(question)
  const labelId = `${fieldId}-label`
  const errorId = `${fieldId}-error`
  const options = toSelectOptions(question)
  return (
    <div className={classes.field}>
      <label id={labelId} htmlFor={fieldId} className={classes.label}>
        <Typography
          variant="body"
          element="span"
          className={classNames(classes.labelText, { [classes.labelError]: !!error })}
        >
          <QuestionLabel question={question} />
        </Typography>
      </label>
      {/* Always set: toggling it would swap SelectField's input mid-focus; an absent id is ignored. */}
      <SelectField
        inputId={fieldId}
        aria-labelledby={labelId}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        value={options.find((option) => option.value === value) ?? null}
        options={options}
        placeholder={SELECT_ONE_LABEL}
        closeMenuOnSelect={true}
        onChange={(option: SelectOption | null) => onChange(option?.value || null)}
        onBlur={onBlur}
      />
      {error && (
        <Typography id={errorId} className={classes.error} variant="body">
          {error}
        </Typography>
      )}
    </div>
  )
}

function MultipleChoiceQuestionField({ question, value, error, onChange, onBlur }: BookingQuestionFieldProps) {
  return (
    <RadioGroup
      id={questionFieldId(question)}
      legend={<QuestionLabel question={question} />}
      options={toChoiceOptions(question)}
      value={typeof value === 'string' ? value : null}
      onChange={onChange}
      onBlur={onBlur}
      error={error ?? undefined}
      required={question.required}
    />
  )
}

function CheckboxesQuestionField({ question, value, error, onChange, onBlur }: BookingQuestionFieldProps) {
  return (
    <CheckboxGroup
      id={questionFieldId(question)}
      legend={<QuestionLabel question={question} />}
      options={toChoiceOptions(question)}
      value={Array.isArray(value) ? value : []}
      onChange={(ticked) => onChange(ticked.length > 0 ? ticked : null)}
      onBlur={onBlur}
      error={error ?? undefined}
    />
  )
}

function QuestionLabel({ question }: { question: BookingQuestion }) {
  return (
    <>
      {question.label}
      {question.required && <RequiredMark className={classes.inlineRequiredMark} />}
    </>
  )
}

function RequiredMark({ className }: { className?: string }) {
  return (
    <span className={classNames(classes.requiredMark, className)} aria-hidden="true">
      *
    </span>
  )
}

function CharacterCounter({ count, limit }: { count: number; limit: number }) {
  return (
    <span className={classNames(classes.counter, { [classes.counterAtLimit]: count >= limit })}>
      {count.toLocaleString('en-US')} / {limit.toLocaleString('en-US')}
    </span>
  )
}

function questionFieldId(question: BookingQuestion): string {
  return `booking-question-${question.id}`
}

function describedBy(...ids: Array<string | null>): string | undefined {
  return ids.filter(Boolean).join(' ') || undefined
}

function toChoiceOptions(question: BookingQuestion): SelectOption[] {
  return (question.options ?? []).map((option) => ({ value: option, label: option }))
}

/** Only an optional dropdown can go back to "Select one" (owner D17). */
function toSelectOptions(question: BookingQuestion): SelectOption[] {
  const options = toChoiceOptions(question)
  return question.required ? options : [{ value: '', label: SELECT_ONE_LABEL }, ...options]
}
